import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { differenceInMinutes } from 'date-fns';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ScarcityIcon } from '#src/atoms/icons/ScarcityIcon/index.tsx';
import { SorareLogo } from '#src/atoms/icons/SorareLogo/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconLink } from '#src/atoms/icons/system/iconLink.ts';
import { Actions } from '#src/atoms/layout/Actions/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BodyS,
  BrandL,
  BrandXL,
  BrandXS,
  HeadlineL,
} from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { MonetaryRewardDisclaimer } from '#src/components/jonum/MonetaryRewardDisclaimer/index.tsx';
import { DialogKey } from '#src/components/navigation/WithDialogs/index.tsx';
import { AnyRewardConfig } from '#src/components/rewards/AnyRewardConfig/index.tsx';
import { Avatar } from '#src/components/user/Avatar/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useCloseDialog } from '#src/hooks/navigation/useCloseDialog.ts';
import { useDialogParam } from '#src/hooks/navigation/useDialogParam.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { formatScarcity } from '#src/lib/cards.ts';
import { glossary } from '#src/lib/glossary.ts';
import { formatGqlErrors, isType } from '#src/lib/gql/index.ts';
import { mergeRewardsConfig } from '#src/lib/rewards.tsx';

import type {
  CreateConnectedTasks,
  CreateConnectedTasksVariables,
} from './__generated__/index.graphql.ts';
import bg from './assets/bg.webp';

const Header = styled(Vertical)`
  height: 40px;
  background: var(--background-image);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const Targets = styled(Vertical)`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-black);
  border-radius: var(--double-unit);
`;

const SAHorizontal = styled(Horizontal)`
  justify-content: space-around;
  width: 100%;
`;

const StyledDialog = styled(Dialog)`
  background: url(${bg}) no-repeat center center / cover;
`;

const StyledScarcityIcon = styled(ScarcityIcon)`
  transform: rotate(20deg);
`;

const CREATE_CONNECTED_TASK_MUTATION = gql`
  mutation CreateConnectedTasks($input: createConnectedTasksInput!) {
    createConnectedTasks(input: $input) {
      tasks {
        id
        target
        name
        title
        rarity
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
          ...mergeRewardsConfig_anyRewardConfigInterface
        }
        ... on ThresholdsDuoConnectionTask {
          id
          connectedUser {
            slug
            ...Avatar_user
          }
        }
      }
      errors {
        message
      }
    }
  }
  ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ${Avatar.fragments.user}
  ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<CreateConnectedTasks, CreateConnectedTasksVariables>;

export const JoinConnectedTaskDialog = () => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { currentUser } = useCurrentUserContext();
  const [tasksPreview, setCreateConnectedTasksResultPreview] =
    useState<CreateConnectedTasks['createConnectedTasks']>();
  const closeDialog = useCloseDialog();

  const connectCampaign = useDialogParam(DialogKey.connect_campaign);
  const connectedUserSlug = useDialogParam(DialogKey.connected_user_slug);
  const [now] = useState(() => new Date());
  const hasJustSignup =
    currentUser && differenceInMinutes(now, currentUser?.createdAt) < 5;

  const [createConnectedTasks, { loading, called }] = useMutation(
    CREATE_CONNECTED_TASK_MUTATION,
    { showErrorsInForm: true }
  );

  const onClose = useCallback(() => {
    closeDialog(
      DialogKey.connected_user_slug,
      [DialogKey.connect_campaign],
      true
    );
  }, [closeDialog]);

  const createTasks = async (simulate: boolean) => {
    if (!connectCampaign || !connectedUserSlug) {
      return;
    }

    const { data } = await createConnectedTasks({
      variables: {
        input: {
          campaignSlug: connectCampaign,
          connectedUserSlug,
          simulate,
        },
      },
    });

    if (simulate) {
      setCreateConnectedTasksResultPreview(data?.createConnectedTasks);
    } else {
      if ((data?.createConnectedTasks?.errors.length || 0) > 0) {
        return;
      }
      onClose();
    }
  };
  const createTasksEvent = useEffectEvent(createTasks);

  useEffect(() => {
    if (connectCampaign && connectedUserSlug && !called && !hasJustSignup) {
      createTasksEvent(true);
    }
  }, [called, connectCampaign, connectedUserSlug, hasJustSignup]);

  if (!connectedUserSlug || !connectCampaign || !called) {
    return null;
  }

  const connectedUser = tasksPreview?.tasks?.find(
    task => task?.__typename === 'ThresholdsDuoConnectionTask'
  )?.connectedUser;

  const sumCashRewards = mergeRewardsConfig(
    tasksPreview?.tasks?.flatMap(task => task?.rewardConfigs) || []
  ).filter(reward => isType(reward, 'MonetaryRewardConfig'))[0];

  if (hasJustSignup) {
    onClose();
    return null;
  }

  return (
    <StyledDialog
      open={!!currentUser && !!tasksPreview}
      onClose={onClose}
      hideHeader
      maxWidth="xs"
      footer={
        tasksPreview?.errors.length === 0 && (
          <Actions direction="horizontal" gap={1}>
            <Button color="red" size="medium" fullWidth onClick={onClose}>
              <FormattedMessage {...glossary.reject} />
            </Button>
            <Button
              color="primary"
              size="medium"
              onClick={async () => {
                createTasks(false);
              }}
              loading={loading}
              fullWidth
            >
              <FormattedMessage {...glossary.accept} />
            </Button>
          </Actions>
        )
      }
    >
      {({ CloseButton }) => {
        if (tasksPreview?.errors.length) {
          return (
            <Content>
              <Vertical>
                <HeadlineL>
                  <FormattedMessage defaultMessage="Sorry" />
                </HeadlineL>
                <BodyM color="var(--c-nd-600)">
                  {formatGqlErrors(tasksPreview?.errors)}
                </BodyM>
                <Button color="primary" size="medium" onClick={onClose}>
                  <FormattedMessage {...glossary.gotIt} />
                </Button>
              </Vertical>
            </Content>
          );
        }

        return (
          <>
            <Header>
              <CloseButtonWrapper>
                <CloseButton className="close-button" onClose={onClose} />
              </CloseButtonWrapper>
            </Header>
            <Content gap={5} center className="text-center">
              <CloseButtonWrapper>
                <CloseButton className="close-button" onClose={onClose} />
              </CloseButtonWrapper>
              <Vertical center>
                <SorareLogo size="small" />
                <BrandXL>{tasksPreview?.tasks?.[0]?.title}</BrandXL>
                <BodyM bold>
                  <FormattedMessage defaultMessage="Duo pairing" />
                </BodyM>
              </Vertical>
              <Vertical gap={3} center>
                <Horizontal gap={4}>
                  {!!currentUser && (
                    <Avatar size={8} user={currentUser} rounded />
                  )}
                  <FontAwesomeIcon icon={iconLink} />
                  {!!connectedUser && (
                    <Avatar size={8} user={connectedUser} rounded />
                  )}
                </Horizontal>
                {!!sumCashRewards && (
                  <BodyM color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="Team up and complete the Duo Mission and you can win up to {amount}."
                      values={{
                        amount: (
                          <MonetaryRewardDisclaimer variant="monetary">
                            <FormattedNumber
                              value={
                                toMonetaryAmount(sumCashRewards.monetaryAmount)
                                  .usd
                              }
                              style="currency"
                              currency="USD"
                            />
                          </MonetaryRewardDisclaimer>
                        ),
                      }}
                    />
                  </BodyM>
                )}
              </Vertical>
              <Targets gap={2} center className="text-center">
                <BrandXS>
                  <FormattedMessage defaultMessage="Score targets" />
                </BrandXS>
                <SAHorizontal>
                  {(tasksPreview?.tasks || [])
                    .filter(Boolean)
                    .map(({ rewardConfigs, target, rarity }) => {
                      return (
                        <Vertical key={rewardConfigs?.[0]?.id} gap={2}>
                          <Vertical gap={0.5}>
                            <Horizontal gap={0.5}>
                              <BrandL>{target}</BrandL>
                              <StyledScarcityIcon
                                rarity={rarity || 'mix'}
                                size={2.5}
                              />
                            </Horizontal>
                            <BodyS>{formatScarcity(rarity || 'mix')}</BodyS>
                          </Vertical>
                          <AnyRewardConfig
                            anyRewardConfig={rewardConfigs?.[0]}
                          />
                        </Vertical>
                      );
                    })}
                </SAHorizontal>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Reach the target in the same Game Week as your duo partner" />
                </BodyS>
              </Targets>
            </Content>
          </>
        );
      }}
    </StyledDialog>
  );
};
